import {FeatureFilter} from '@mc/react-mapio';
import {SearchFilterAttributeTargetVO, SearchFilterLayerTargetVO, SearchFilterTargetVO} from '../../../api';
import {CiselnikFilterNode} from './filterTreeTypes';

/** Všechna layerId vrstev (LAYER targetů) ve stromu filtru – bez ohledu na zaškrtnutí. */
export const collectLayerTargetIds = (nodes: CiselnikFilterNode[]): number[] =>
    nodes.flatMap(n => [
        ...(n.target?.kind === 'LAYER' ? [(n.target as SearchFilterLayerTargetVO).layerId] : []),
        ...(n.children ? collectLayerTargetIds(n.children) : []),
    ]);

/** Typ prvku engine `layerConditions` (react-mapio typ `LayerConditionType` není z balíčku exportovaný). */
type EngineLayerConditions = NonNullable<FeatureFilter['layerConditions']>;

/** Výsledek překladu zaškrtnutých listů na aplikovatelný filtr. */
export interface AppliedFilter {
    /** Podmínky pro engine `setFilter({layerConditions})` – odvozené z ATTRIBUTE targetů. */
    layerConditions: EngineLayerConditions;
    /** ID vrstev z LAYER targetů – pro řízení viditelnosti vrstev (napojení zatím TODO). */
    layerIds: number[];
}

/**
 * Přeloží zaškrtnuté {@link SearchFilterTargetVO} na filtr mapového enginu (`layerConditions`).
 *
 * - ATTRIBUTE targety se seskupí podle (`valueType`, `atributName`) a jejich `values` se sjednotí
 *   do jedné engine podmínky. Engine `layerConditions` je plochý (klíč = `atributName`, bez `layerId`),
 *   takže víc vrstev se stejným atributem (např. přístupnost napříč POI vrstvami) splyne do jedné podmínky.
 * - LAYER targety se posbírají do `layerIds` (viditelnost vrstev – zatím neaplikováno, viz volající).
 *
 * Pozn.: `SearchFilterTargetVO.kind` je z generátoru pouze `string` (diskriminátor bez literálu),
 * proto po kontrole `kind` zúžíme přetypováním na konkrétní VO.
 */
export const buildAppliedFilter = (targets: SearchFilterTargetVO[]): AppliedFilter => {
    const byAttr = new Map<string, {type: 'string' | 'integer'; atributName: string; values: Set<string>}>();
    const layerIds = new Set<number>();

    for (const target of targets) {
        if (target.kind === 'LAYER') {
            layerIds.add((target as SearchFilterLayerTargetVO).layerId);
            continue;
        }
        if (target.kind === 'ATTRIBUTE') {
            const t = target as SearchFilterAttributeTargetVO;
            const type = t.valueType === 'integer' ? 'integer' : 'string';
            const key = `${type}::${t.atributName}`;
            let entry = byAttr.get(key);
            if (!entry) {
                entry = {type, atributName: t.atributName, values: new Set<string>()};
                byAttr.set(key, entry);
            }
            (t.values ?? []).forEach(v => entry!.values.add(v));
        }
    }

    const layerConditions: EngineLayerConditions = Array.from(byAttr.values()).map(c =>
        c.type === 'integer'
            ? {type: 'integer', atributName: c.atributName, atributValue: Array.from(c.values).map(Number)}
            : {type: 'string', atributName: c.atributName, atributValue: Array.from(c.values)},
    );

    return {layerConditions, layerIds: Array.from(layerIds)};
};
